/**
 * Sticky-aware section scrolling for form steppers.
 * Matches Import Booking: scroll #main-content with page-header offset
 * so sections land just below the sticky header (not under/past it).
 */

/** Marks the sticky validation summary so scrolls can reserve room for it. */
export const VALIDATION_BANNER_ATTR = "data-validation-banner";

/** Published by FormSectionStepper (0 while the dock is hidden). */
export const FORM_STEPPER_HEIGHT_VAR = "--form-stepper-height";

/**
 * Height of the validation summary while it is pinned under the page header.
 * It sits above the form content, so a target parked at the plain header offset
 * ends up hidden behind it — which reads as the page having scrolled too far.
 * Measured live because the error chips wrap to a variable number of rows.
 */
export function getStickyValidationBannerHeight() {
    if (typeof document === "undefined") return 0;

    let tallest = 0;
    document
        .querySelectorAll(`[${VALIDATION_BANNER_ATTR}]`)
        .forEach((el) => {
            if (el.offsetParent === null) return;
            const { position } = window.getComputedStyle(el);
            if (position !== "sticky" && position !== "fixed") return;
            const { height } = el.getBoundingClientRect();
            if (height > tallest) tallest = height;
        });

    return tallest > 0 ? tallest + 8 : 0;
}

export function getStickyPageScrollOffset(extraPadding = 12) {
    if (typeof document === "undefined") return 80;
    const root = getComputedStyle(document.documentElement);
    const header =
        parseFloat(root.getPropertyValue("--page-header-height")) || 64;
    const importStepper =
        parseFloat(root.getPropertyValue("--import-stepper-height")) || 0;
    const exportStepper =
        parseFloat(root.getPropertyValue("--export-stepper-height")) || 0;
    const formStepper =
        parseFloat(root.getPropertyValue(FORM_STEPPER_HEIGHT_VAR)) || 0;
    return (
        header +
        importStepper +
        exportStepper +
        formStepper +
        getStickyValidationBannerHeight() +
        extraPadding
    );
}

/**
 * Smooth-scroll an element into view under sticky page chrome.
 * @param {HTMLElement | null | undefined} el
 * @param {{ offsetTop?: number, behavior?: ScrollBehavior, expandThenScroll?: boolean }} [options]
 */
export function scrollElementBelowStickyHeader(el, options = {}) {
    if (!el) return;

    const offset =
        typeof options.offsetTop === "number"
            ? options.offsetTop
            : getStickyPageScrollOffset();
    const behavior = options.behavior || "smooth";

    const run = () => {
        const scroller = document.getElementById("main-content");
        if (scroller) {
            const scrollerRect = scroller.getBoundingClientRect();
            const elRect = el.getBoundingClientRect();
            const nextTop =
                scroller.scrollTop + (elRect.top - scrollerRect.top) - offset;
            scroller.scrollTo({ top: Math.max(0, nextTop), behavior });
            return;
        }
        const nextTop =
            el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: Math.max(0, nextTop), behavior });
    };

    // Double rAF so expand/collapse layout settles before measuring.
    requestAnimationFrame(() => {
        requestAnimationFrame(run);
    });
}
